import React, { useState, useEffect, useContext } from "react";
import { Alert, Typography, TextField, Button, Grid } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import rnp from "../RNP/rnp.png";
import { ContextSession } from "../../services/ContextSession";
import { isSessionExpiredError, postHeader } from "../../services/http";
import FichaDefuncion from "./fichadefuncion.component";
import { rnpSubmitButtonSx } from "./rnpButtonStyles";

const JUSTIFICACION_MAX_LENGTH = 100;

async function getDniDefuncion(dni, justificacion, token) {
  const json = { numinscripcion: dni, justificacion };
  const auth = `Bearer ${token}`;
  const url = "https://bei360.injupemp.gob.hn:8444/api/rnp/defunciones/";
  return postHeader(url, json, auth);
}

export default function ConsultaDifuntos() {
  const sessi = useContext(ContextSession);
  const dnida = sessi.getVariable("dniDataDifunto") || null;
  const [dniData, setDniData] = useState(dnida);
  const [dni, setDni] = useState("");
  const [justificacion, setJustificacion] = useState("");
  const [justificacionError, setJustificacionError] = useState(false);
  const [requestError, setRequestError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      sessi.setVariable("dniDataDifunto", null);
    };
  }, []);

  const getWidth = () => (windowWidth <= 600 ? "300px" : "600px");

  const handleDniChange = (event) => {
    const inputDni = event.target.value.replace(/\D/g, "");
    if (!inputDni) {
      setDni("");
      return;
    }

    let formattedDni = inputDni.slice(0, 4);
    if (inputDni.length > 4) formattedDni += `-${inputDni.slice(4, 8)}`;
    if (inputDni.length > 8) formattedDni += `-${inputDni.slice(8, 13)}`;
    setDni(formattedDni);
  };

  const handleJustificacionChange = (event) => {
    const value = event.target.value.slice(0, JUSTIFICACION_MAX_LENGTH);
    setJustificacion(value);
    if (justificacionError && value.trim()) {
      setJustificacionError(false);
    }
  };

  async function handleConsultar() {
    const justificacionLimpia = justificacion.trim();
    if (!justificacionLimpia) {
      setJustificacionError(true);
      return;
    }
    if (dni.length < 15) return;

    setIsLoading(true);
    setRequestError("");
    setDniData(null);
    sessi.setVariable("dniDataDifunto", null);

    try {
      const id = dni.replaceAll("-", "");
      const token = sessi.sessionData.token;
      const data = await getDniDefuncion(id, justificacionLimpia, token);
      setDniData(data);
      sessi.setVariable("dniDataDifunto", data);
    } catch (err) {
      if (isSessionExpiredError(err)) {
        return;
      }

      const msg =
        err?.response?.data?.error ||
        err?.data?.error ||
        err?.payload?.error ||
        err?.message ||
        "No se pudo completar la consulta.";

      setRequestError(msg);
      const fallback = { error: msg };
      setDniData(fallback);
      sessi.setVariable("dniDataDifunto", fallback);
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <Grid container direction="column" alignItems="center" spacing={2}>
      <Grid item marginTop={3}>
        <Typography variant="h3">Consulta Difuntos</Typography>
      </Grid>

      <Grid item container alignItems="center" justifyContent="center" spacing={2}>
        <Grid item>
          <Typography variant="body1">
            <img src={rnp} alt="RNP Logo" height="70" />
          </Typography>
        </Grid>

        <Grid item>
          <Grid container direction="column" spacing={2}>
            <Grid item>
              <TextField
                fullWidth
                id="dni-input"
                label="DNI"
                variant="outlined"
                value={dni}
                onChange={handleDniChange}
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                style={{ width: getWidth() }}
              />
            </Grid>
            <Grid item>
              <TextField
                fullWidth
                multiline
                minRows={2}
                maxRows={3}
                id="justificacion-input"
                label="Justificación breve"
                variant="outlined"
                value={justificacion}
                onChange={handleJustificacionChange}
                inputProps={{ maxLength: JUSTIFICACION_MAX_LENGTH }}
                error={justificacionError}
                helperText={
                  justificacionError
                    ? "Debe indicar la justificación de la consulta."
                    : `Motivo laboral de la consulta. ${justificacion.length}/${JUSTIFICACION_MAX_LENGTH}`
                }
                style={{ width: getWidth() }}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item>
          <Button
            onClick={handleConsultar}
            disabled={dni.length !== 15 || !justificacion.trim() || isLoading}
            variant="contained"
            sx={rnpSubmitButtonSx}
          >
            Enviar
          </Button>
        </Grid>
      </Grid>

      <Grid item>{isLoading && <CircularProgress color="success" />}</Grid>
      <Grid item>{isLoading && <Typography>Cargando...</Typography>}</Grid>
      <Grid item>{requestError && <Alert severity="warning">{requestError}</Alert>}</Grid>

      {dniData != null && <FichaDefuncion dniData={dniData} />}
    </Grid>
  );
}
